import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { FaUsers, FaSearch, FaMapMarkerAlt } from 'react-icons/fa';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';
import { isSignedIn } from '../_helpers/safeRedirect.js';
import '../styles/PerformersSearchPage.css';
import MainNavigation from './MainNavigation.jsx';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import { useT } from '../contexts/LocaleContext.jsx';
import '../App.css';

const BandCard = ({ band }) => {
  const t = useT();
  return (
    <div className="psp__band-card">
      <Link to={`/performers/${band.ID}`} className="psp__band-card-link" data-testid="band-card">
        <div className="psp__band-avatar">
          {band.ProfileImageURL ? (
            <img
              src={band.ProfileImageURL}
              alt={t('performersSearch.profileImageAlt', { name: band.Name })}
              className="psp__band-avatar-img"
            />
          ) : (
            <FaUsers className="psp__band-avatar-icon" aria-hidden="true" />
          )}
        </div>
        <div className="psp__band-name">{band.Name}</div>
      </Link>
    </div>
  );
};

function PerformersSearchPage() {
  const [searchParams] = useSearchParams();
  const initialName = searchParams.get('name') ?? '';
  const [bandName, setBandName] = useState(initialName);
  const [location, setLocation] = useState('');
  const [myBands, setMyBands] = useState([]);
  const [recommendedBands, setRecommendedBands] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const navigate = useNavigate();
  const t = useT();
  const fetchWrapperRef = useRef(useFetchWrapper());

  // My bands and the recommendations load independently (ENG-514): a signed-out
  // visitor has no bands to ask for, and a mybands failure used to take the
  // recommendations down with it.
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const [mine, recommended] = await Promise.allSettled([
      isSignedIn() ? fetchWrapperRef.current.get(`${config.apiBaseUrl}/user/mybands`) : Promise.resolve([]),
      fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/recommended`),
    ]);

    if (mine.status === 'fulfilled') {
      setMyBands(Array.isArray(mine.value) ? mine.value : []);
    } else {
      console.error('Error fetching my bands:', mine.reason);
      setMyBands([]);
    }

    if (recommended.status === 'fulfilled') {
      const recommendedResponse = recommended.value;
      if (recommendedResponse?.success && Array.isArray(recommendedResponse.bands)) {
        setRecommendedBands(
          recommendedResponse.bands.map((band) => ({
            ID: band.id,
            Name: band.name,
            ProfileImageURL: band.profile_image_url,
            Location: band.location,
            Description: band.description,
            SocialMediaLinks: band.social_media_links,
            WebsiteURL: band.website_url,
            ContactEmail: band.contact_email,
          }))
        );
      } else {
        setRecommendedBands([]);
      }
    } else {
      console.error('Error fetching recommended bands:', recommended.reason);
      setRecommendedBands([]);
    }

    if (mine.status === 'rejected' || recommended.status === 'rejected') {
      setError({ key: 'performersSearch.loadError' });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runSearch = useCallback(async (name, loc) => {
    setSearchError(null);
    setIsSearching(true);
    try {
      const params = new URLSearchParams();
      if (name) params.set('name', name);
      if (loc) params.set('location', loc);
      const response = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/search/bands?${params.toString()}`
      );
      const bands = Array.isArray(response)
        ? response
        : response?.bands && Array.isArray(response.bands)
        ? response.bands.map((band) => ({
            ID: band.id,
            Name: band.name,
            ProfileImageURL: band.profile_image_url,
          }))
        : [];
      setSearchResults(bands);
    } catch (err) {
      console.error('Error searching bands:', err);
      setSearchError({ key: 'performersSearch.searchError' });
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!bandName && !location) {
      setSearchError({ key: 'performersSearch.fillOneField' });
      return;
    }
    runSearch(bandName, location);
  };

  // Arriving with ?name= (e.g. from the home page nav search) runs that search
  // immediately; a later ?name= change while already here re-seeds the field too.
  useEffect(() => {
    if (!initialName) return;
    setBandName(initialName);
    runSearch(initialName, '');
  }, [initialName, runSearch]);

  if (isLoading) {
    return (
      <div className="performers-search-page">
        <div className="performers-search-page-header">
          <StreamPageHeader />
        </div>
        <div className="performers-search-page-wrapper">
          <div className="performers-search-page-featured-area">
            <div className="psp">
              <div className="psp__loading">
                <LoadingSpinner />
                <p>{t('common.status.loadingEllipsis')}</p>
              </div>
            </div>
            <MainNavigation />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="performers-search-page">
      <div className="performers-search-page-header">
        <StreamPageHeader />
      </div>
      <div className="performers-search-page-wrapper">
        <div className="performers-search-page-featured-area">
          <div className="psp">
            {error && (
              <div className="psp__error" role="alert" data-testid="psp-load-error">
                <span>{t(error.key)}</span>
                <button className="psp__error-retry" onClick={fetchData}>
                  {t('common.actions.retry')}
                </button>
              </div>
            )}

            {(recommendedBands.length > 0 || myBands.length > 0) && (
              <div className="psp__bands-container">
                {recommendedBands.length > 0 && (
                  <section className="psp__bands-section" data-testid="psp-recommended">
                    <h2 className="psp__section-title">{t('performersSearch.youMayEnjoy')}</h2>
                    <div className="psp__band-grid">
                      {recommendedBands.map((band) => (
                        <BandCard key={band.ID} band={band} />
                      ))}
                    </div>
                  </section>
                )}
                {myBands.length > 0 && (
                  <section className="psp__bands-section" data-testid="psp-my-bands">
                    <h2 className="psp__section-title">{t('performersSearch.myPerformingGroups')}</h2>
                    <div className="psp__band-grid">
                      {myBands.map((band) => (
                        <BandCard key={band.ID} band={band} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}

            <div className="psp__search-panel">
              <div className="psp__search-header">
                <h2 className="psp__title">{t('performersSearch.title')}</h2>
                <button
                  className="psp-btn psp-btn--secondary"
                  onClick={() => navigate('/register-band')}
                  data-testid="psp-register-band"
                >
                  {t('performersSearch.registerPrompt')}
                </button>
              </div>

              {searchError && (
                <div className="psp__search-error" role="alert" data-testid="psp-search-error">
                  {t(searchError.key)}
                </div>
              )}

              <form className="psp__search-form" onSubmit={handleSearch} data-testid="psp-search-form">
                <div className="psp__field">
                  <label htmlFor="bandName" className="psp__label">
                    {t('performersSearch.nameLabel')}
                  </label>
                  <div className="psp__input-wrap">
                    <FaSearch className="psp__input-icon" aria-hidden="true" />
                    <input
                      id="bandName"
                      type="text"
                      value={bandName}
                      onChange={(e) => {
                        setBandName(e.target.value);
                        if (searchError) setSearchError(null);
                      }}
                      className="psp__input"
                      placeholder={t('performersSearch.namePlaceholder')}
                    />
                  </div>
                </div>
                <div className="psp__field">
                  <label htmlFor="location" className="psp__label">
                    {t('performersSearch.locationLabel')}
                  </label>
                  <div className="psp__input-wrap">
                    <FaMapMarkerAlt className="psp__input-icon" aria-hidden="true" />
                    <input
                      id="location"
                      type="text"
                      value={location}
                      onChange={(e) => {
                        setLocation(e.target.value);
                        if (searchError) setSearchError(null);
                      }}
                      className="psp__input"
                      placeholder={t('performersSearch.locationPlaceholder')}
                    />
                  </div>
                </div>
                <button type="submit" className="psp-btn psp-btn--primary" disabled={isSearching} data-testid="psp-search-submit">
                  {isSearching ? t('performersSearch.searching') : t('common.actions.search')}
                </button>
              </form>

              {searchResults !== null && (
                <div className="psp__search-results" data-testid="psp-search-results">
                  {searchResults.length === 0 ? (
                    <p className="psp__search-no-results" data-testid="psp-no-results">{t('performersSearch.noResults')}</p>
                  ) : (
                    <div className="psp__band-grid">
                      {searchResults.map((band) => (
                        <BandCard key={band.ID} band={band} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <MainNavigation />
        </div>
      </div>
    </div>
  );
}

export default PerformersSearchPage;
